// @flow
import { isString } from "voca";
import { some } from "lodash";
import { getLogger, EXT_EVENTS } from "@cld/upload-widget";
import appConfig from "appConfig";
import { doLater } from "utils/domUtils";
import composeEmitter from "common/emitter";
import { throwOnDev } from "utils/envUtils";
import { EVENT_TYPES } from "./common/events";
import { getUploadFieldFromContainer } from "./common/utils";
import consts from "./common/consts";
import initUploadField from "./fileUpload/uploadField";
import setCallbacks from "./fileUpload/uploadCallbacks";
import { unsetFilePreCallsDelayed } from "./fileUpload/uploadFileMethods";
import { types as ats } from "./store/actions";
import {
	selectLatestConfig,
	selectUploadOptions,
	selectWidgetId,
	selectShowOnStart,
	selectLastQueueMeta,
	selectQueues,
	selectUploadsData,
} from "./store/selectors";
import initStateChangeProcessors from "./stateProcessors";
import getQueuesManager from "./queuesManager";
import getBatchHandler from "./batchHandler";

import type {
	ExternalInterface, UploadField, Uploader, AddToQueueType,
} from "./types";
import type { HandleResponse } from "./batchHandler";

const logger = getLogger();

type UploaderOptions = {|
	actions: { [string]: Function },
	getState: Function,
	externalApi: ExternalInterface,
	store: Object,
|};

export default (options: UploaderOptions): Uploader => { // creates an uploader instance
	const { getState, actions, externalApi } = options;

	let id: string;
	let fieldContainerElm: ?HTMLElement = null;
	let fileUpload: ?UploadField = null;
	let fileUploadCallbacks = null;
	let stateChangeProcessors = null;
	let loadedQueueId = null;

	const uploaderEmitter = composeEmitter();

	const getWidgetId = () => {
		if (!id) {
			id = selectWidgetId(getState());
		}
		return id;
	};

	const sendExternalEvent = (data) => externalApi.emitEvent({
		widgetId: getWidgetId(),
		...data,
	});

	const queuesManager = getQueuesManager({ ...options, sendExternalEvent });

	const getQueueForId = (queueId) => selectQueues(getState())
		.find((q) => q.queueId === queueId);

	const getLoadedQueueId = () => loadedQueueId;

	const abortUploads = (ids: string[]) => queuesManager.abortUploads(ids);

	const notifyBatchStarted = () => {
		uploaderEmitter.emit(EVENT_TYPES.UPLOADER_BATCH_STARTED);
		sendExternalEvent({ event: EXT_EVENTS.QUEUES_START, info: {} });
	};

	const internalAddToQueue = (info: AddToQueueType) => {
		if (fileUpload) {
			const field = getUploadFieldFromContainer(fileUpload.container);

			logger.log(`[uploader]: about to call upload field add with ${info.files.length} files`);

			info.files.forEach((f) => {
				doLater(() => { // separate loading of each file to throttle cpu load
					try {
						field.fileupload("add", { files: [f] });
					} catch (ex) {
						logger.warn("[uploader]: received error when using 'add' method of file upload field", ex);
						throwOnDev(ex);
					}
				}, 10);
			});
		}
	};

	const getConfigFromQueue = (queue) => queue.configOverride || queue.config;

	const getLatestConfig = () => {
		let config;
		const loadedQueueId = getLoadedQueueId();

		if (loadedQueueId) {
			const queue = getQueueForId(loadedQueueId);
			config = getConfigFromQueue(queue);
		} else {
			config = selectLatestConfig(getState());
		}

		return config;
	};

	const getMutableConfig = () => {
		const config = getLatestConfig();
		return config && config.asMutable({ deep: true });
	};

	const getUploadOptions = () => selectUploadOptions(getState());

	const notifyBatchCancelled = (reason: ?string, notifyExtOnCancel = false, info: ?AddToQueueType) => {
		uploaderEmitter.emit(EVENT_TYPES.UPLOADER_BATCH_CANCELLED, { reason, info }); // notify UI side that batch was cancelled
		// send implementation side queue end to match the queue start that was sent before
		sendExternalEvent({ event: EXT_EVENTS.QUEUES_END, info: {} });

		if (notifyExtOnCancel) {
			// in case it was a internal cancel (not from pre-batch callback) then notify implementation side on the cancel reason
			sendExternalEvent({ event: EXT_EVENTS.BATCH_CANCELED, info: { reason } });
		}
	};

	const batchHandler = getBatchHandler({
		...options,
		abortUploads,
		getLoadedQueueId,
		queuesManager,
		getLatestConfig,
		getWidgetId,
		getUploadOptions,
		notifyBatchCancelled: () => notifyBatchCancelled(consts.BATCH_CANCEL_REASONS.INTEGRATION),
		actions,
	});

	const processBatchHandleResponse = (response: HandleResponse, info: AddToQueueType) => {
		if (response.cancelled) {
			logger.log(`[uploader]: batch handling cancelled because of: ${response.reason || ""}`);
			notifyBatchCancelled(response.reason, true, info);
		} else  {
			notifyBatchStarted();
		}

		return response.result;
	};

	const getUploadFilesCancel = (files) =>
		() => {
			logger.log(`[uploader]: silently cancelling upload for ${files.length} files`);
			options.actions[ats.CANCEL_UPLOADER_FILES]({ files });
		};

	const handleMetadataRequired = async (info: AddToQueueType) => {
		logger.log(`[uploader]: metadata is required. postponing ${info.files.length} files batch until schema is provided and values filled`);
		const schemaData = await batchHandler.handleMetadataSchemaRetrieval(info);

		if (schemaData?.schema) {
			uploaderEmitter.emit(EVENT_TYPES.UPLOADER_MD_STARTED, {
				...schemaData,
				files: info.files,
				cropInfo: info.cropInfo,
				//provide a way to silently cancel the uploads in case user closes metadata form
				cancelUpload: getUploadFilesCancel(info.files),
			});
		}
		else if (schemaData?.cancel !== true) {
			logger.log("[uploader]: metadata callback didnt provide schema - continue with upload as normal");

			unsetFilePreCallsDelayed(info.files.map((f) => f.id), { actions });

			await handleBatchAdded({
				...info,
				//provide dummy metadata so batch handler can continue (prepre/prebatch handling)
				metadata: consts.NO_METADATA_REQUIRED
			});
		}
	};

	const handleBatchAdded = async (info: AddToQueueType, fromField: boolean = false, existingBatch: ?boolean = false) => {
		let continueToAdd = false;
		const config = getLatestConfig();

		const handleResponse = await batchHandler.handleBatchAdded(info, existingBatch);

		if (!existingBatch && !info.metadata && config.useMetadataCallback) {
			if (!handleResponse.cancelled) {
				await handleMetadataRequired(info);
			}
		}
		else {
			logger.log(`[uploader]: handling batch addition - of ${info.files.length} files`, {
				fromField,
				existingBatch
			});

			continueToAdd = processBatchHandleResponse(handleResponse, info);
		}

		// continueToAdd can be false when waiting for prebatch callback (ex: signature or metadata)
		if (fromField && continueToAdd) {
			//files added directly to the upload field should be added back immediately
			internalAddToQueue(info);
		}

		return continueToAdd;
	};

	const handleCropAdded = (info: AddToQueueType) => {
		uploaderEmitter.emit(EVENT_TYPES.UPLOADER_CROP_STARTED, info);
		return Promise.resolve(false); // crop makes the upload wait
	};

	const bypassCropForUrl = (info: AddToQueueType, config) => {
		return config && info.files && info.files.length && isString(info.files[0])
			&& ((config.secure && /^http:\/\//.test(info.files[0])) ||
				(!config.secure && /^https:\/\//.test(info.files[0])) ||
				/(\.eps|\.mov|\.mp4)$/.test(info.files[0]) ||
				/^https:\/\/delivery.gettyimages.com\/downloads\/\S*&videos/.test(info.files[0]) || //gettyimages integration doesn't return file extension
				/^https:\/\/media.istockphoto.com\/videos/.test(info.files[0])//iStock integration doesn't return file extension
			);
	};

	const bypassCropForFile = (info: AddToQueueType) => {
		const file = info?.files[0];
		const type = file?.type;

		const isStringFile = isString(file) || isString(file?.file);

		return (!isStringFile && !type) ||
			some(appConfig.crop.blackList, (pattern) => new RegExp(pattern).test(type));
	};// best effort to bypass crop for non-image files

	const shouldUseCroppingHandling = (info: AddToQueueType) => {
		const config = getLatestConfig();

		return config && config.cropping && // if cropping is truthy we should use crop
			 !bypassCropForFile(info) &&
			 !bypassCropForUrl(info, config) &&
			 !(info.cropInfo && info.cropInfo.length);
	};

	/**
	 * handle upload file(s) being added to queue (could be before crop or after crop)
	 * @param info
	 * @param fromField - means the handleUpload was called by the jquery file upload add handler (filesystem or d&d)
	 * @param existingBatch - means the image upload was stopped after it was processed and needs now to go through batch handler again
	 */
	const handleUpload = (info: AddToQueueType, fromField: boolean = false, existingBatch: ?boolean = false) => (
		shouldUseCroppingHandling(info) ?
			handleCropAdded(info) :
			handleBatchAdded(info, fromField, existingBatch));

	const addToQueue = async (info: AddToQueueType) => {
		const continueAdd = await handleUpload(info);

		if (continueAdd) {
			logger.log("[uploader]: received info for programmatically added upload: ", info);
			internalAddToQueue(info); // addition to the queue will happen once the next queue is loaded and upload field is re-rendered
		} else {
			logger.log("[uploader]: delaying addition of files to queue as it will be loaded once the upload field is re-rendered");
		}
	};

	const updatePreparedParams = (params: Object) => fileUpload && fileUpload.updatePreparedParams(params);

	const getCallbackOptions = (config) => ({
		widgetId: getWidgetId(),
		getUploadOptions,
		sendExternalEvent,
		handleUpload,
		updatePreparedParams,
		...options,
		config,
	});

	const clearQueues = async () => await queuesManager.clearQueues();

	const renderUploadField = (fieldContainer: HTMLElement, ddZone: HTMLElement) => {
		if (!fileUpload) {
			logger.log("[uploader]: rendering upload field");

			const lastQueue = selectLastQueueMeta(getState()); // first time loading queue and its configuration
			const config = getConfigFromQueue(lastQueue);
			const mutableConfig = config.asMutable();

			loadedQueueId = lastQueue.id;

			fileUpload = initUploadField(fieldContainer, ddZone, mutableConfig);
			fileUploadCallbacks = setCallbacks(fileUpload.container, getCallbackOptions(mutableConfig));
			fieldContainerElm = fieldContainer;
		}
	};

	const getShowOnStart = () => selectShowOnStart(getState());

	return {
		...uploaderEmitter,

		isShowingOnStart: getShowOnStart,

		// todo: remove and make config available to the connector as a parameter passed to the UPLOADER_READY & UPLOADER_UPDATE_SETTINGS events
		// return a mutable copy of the config
		getConfig: getMutableConfig,

		isReady: () => !!getLatestConfig(),

		getLoadedQueueId,

		clearQueues,

		addToQueue,

		renderUploadField,

		abortUploads,

		loadFilesFromActiveQueue() {
			const files = queuesManager.getPausedFilesFromActiveQueue();

			if (files && files.length) {
				logger.log(`[uploader]: adding ${files.length} paused files to upload from active queue`);
				internalAddToQueue({ files });
			}
		},

		loadFilesFromRunningQueue() {
			const files = queuesManager.getPausedFilesFromRunningQueue();

			if (files && files.length) {
				internalAddToQueue({ files });
			}
		},

		showFileDialog() {
			if (fieldContainerElm) {
				getUploadFieldFromContainer(fieldContainerElm).trigger("click");
			}
		},

		processStateChange(newState, prevState) {
			if (!stateChangeProcessors) {
				stateChangeProcessors = initStateChangeProcessors(this, sendExternalEvent, actions);
			}

			stateChangeProcessors(newState, prevState);
		},

		reRenderUploadField(queueId: string) {
			const loadedQueue = getQueueForId(queueId);
			const config = loadedQueue.configOverride || loadedQueue.config;

			fileUpload && fileUpload.update(config.asMutable()); // written like this for flow
			fileUploadCallbacks && fileUploadCallbacks.useCustomAddMethod(); // written like this for flow

			loadedQueueId = loadedQueue.queueId;
		},

		getHasRenderedUploadField() {
			return !!fieldContainerElm;
		},

		setCurrentSource(source: string) {
			actions[ats.SET_UPLOADER_CURRENT_SOURCE]({ source });
			sendExternalEvent({ event: EXT_EVENTS.SOURCE_CHANGED, info: { source } });
		},

		retrieveClientTags(prefix) {
			options.externalApi.requestTags({
				widgetId: getWidgetId(),
				prefix,
			});
		},

		retrieveClientUploadPresets() {
			options.externalApi.requestUploadPresets({
				widgetId: getWidgetId(),
			});
		},

		setUploadTags(tags) {
			actions[ats.SET_UPLOADER_TAGS]({ tags });
			sendExternalEvent({ event: EXT_EVENTS.TAGS, info: { tags } });
		},

		setUploadPreset(uploadPreset) {
			actions[ats.SET_UPLOADER_UPLOAD_PRESET]({ uploadPreset });
			sendExternalEvent({ event: EXT_EVENTS.UPLOAD_PRESETS, info: { uploadPreset } });
		},

		setUploadPublicId(id, cleanAfterUpload) {
			actions[ats.SET_UPLOADER_PUBLICID]({ id, cleanAfterUpload });
			sendExternalEvent({ event: EXT_EVENTS.PUBLICID, info: { id } });
		},

		retryUploads(ids: string[]) {
			queuesManager.processRetries(ids);
		},

		hide() {
			options.externalApi.sendHide({
				widgetId: getWidgetId(),
			});
		},

		setWidgetViewType(type: number) {
			sendExternalEvent({ event: EXT_EVENTS.WIDGET_VIEW_TYPE_CHANGE, info: { type } });
		},

		sendShowCompleted(ids: string[]) {
			const uploadsData = selectUploadsData(getState());
			const items = Object.keys(uploadsData)
				.reduce((res, id) => (~ids.indexOf(id) ? res.concat(uploadsData[id]) : res), []);

			sendExternalEvent({ event: EXT_EVENTS.SHOW_COMPLETED, info: { items } });
		},

		getStore() { // here for debugging purposes
			return options.store;
		},

		uploadFieldClick() {
			if (fileUpload) {
				const field = getUploadFieldFromContainer(fileUpload.container);
				field.click();
			}
		},
	};
};
